import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const navItems = [
  { to: '/create-document', icon: '➕', labelKey: 'nav.createDocument' },
  { to: '/documents', icon: '📄', labelKey: 'nav.documents' },
  { to: '/customers', icon: '👥', labelKey: 'nav.customers' },
  { to: '/items', icon: '📦', labelKey: 'nav.items' },
  { to: '/helper', icon: '🫧', labelKey: 'nav.dadaHelper' },
  { to: '/reports', icon: '📊', labelKey: 'nav.reports' },
  { to: '/settings', icon: '⚙️', labelKey: 'nav.settings' },
];

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHelperRoute = location.pathname === '/helper';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <div className="flex h-full">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 z-40 flex flex-col bg-gray-900 text-white transition-all duration-200
          ltr:left-0 rtl:right-0
          ${sidebarWidth}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          rtl:${mobileOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="flex h-14 items-center gap-2 px-4 border-b border-gray-800 shrink-0">
          <span className="text-xl">🫧</span>
          {!collapsed && (
            <span className="font-semibold text-sm whitespace-nowrap">
              {t('common.appName')}
            </span>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => {
                setMobileOpen(false);
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-600/30 text-primary-300 border-primary-400 ltr:border-l-[3px] rtl:border-r-[3px]'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <span className="text-lg shrink-0">{item.icon}</span>
              {!collapsed && (
                <span className="truncate">{t(item.labelKey)}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-10 border-t border-gray-800 text-gray-500 hover:text-white transition-colors"
          aria-label="Toggle sidebar"
        >
          {collapsed ? '»' : '«'}
        </button>
      </aside>

      {/* Main wrapper */}
      <div
        className={`flex flex-1 flex-col min-h-0 transition-all duration-200
          ltr:lg:ml-16 rtl:lg:mr-16
          ${!collapsed ? 'ltr:lg:ml-60 rtl:lg:mr-60' : ''}
        `}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4 shrink-0">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-gray-600 hover:text-gray-900"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          <div className="flex items-center gap-3 ltr:ml-auto rtl:mr-auto">
            <LanguageSwitcher />

            {/* User info */}
            {user && (
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="hidden sm:inline text-sm text-gray-700 font-medium">
                  {user.name}
                </span>
              </div>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
              aria-label="Logout"
            >
              ↪
            </button>
          </div>
        </header>

        {/* Main content */}
        <main
          className={`flex-1 overflow-y-auto scrollbar-thin ${
            isHelperRoute ? 'bg-white p-0' : 'bg-gray-50 p-4 md:p-6'
          }`}
        >
          <Outlet />
        </main>
      </div>



    </div>
  );
}
